import React from 'react';
import { BookOpen, Zap, Clock, TrendingUp, Layers, BookCheck, Timer, Library } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from 'recharts';
import { formatTimeSaved, calculateImprovement } from '../../utils/stats';

interface UpsellStatsProps {
  wordsRead: number;
  maxWpm: number;
  avgWpm: number;
  timeSaved: number;
  sessions: number;
  booksCompleted: number;
  genresRead: string[];
  timeSpentSeconds: number;
}

const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export const UpsellStats: React.FC<UpsellStatsProps> = ({
  wordsRead,
  maxWpm,
  avgWpm,
  timeSaved,
  sessions,
  booksCompleted,
  genresRead,
  timeSpentSeconds,
}) => {
  const improvement = calculateImprovement(avgWpm);
  const displayAvgWpm = avgWpm > 0 ? avgWpm : 0;
  const displayMaxWpm = maxWpm > 0 ? maxWpm : 500;
  const avgSessionMinutes = sessions > 0 ? Math.round(timeSpentSeconds / sessions / 60) : 0;

  const speedData = [
    { name: 'Baseline WPM', wpm: 200 },
    { name: 'New Avg WPM', wpm: displayAvgWpm || 200 },
    { name: 'Peak WPM', wpm: displayMaxWpm },
  ];

  return (
    <div className="mb-16">
      {/* Improvement badge */}
      {improvement > 0 && (
        <div className="text-center mb-6">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm font-semibold">
            <TrendingUp className="w-4 h-4" />
            +{improvement}% speed increase
          </span>
        </div>
      )}

       {/* Bar chart card */}
       <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm mb-6">
         <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-6">
           Session Performance
         </h3>
         <div className="h-64">
           <ResponsiveContainer width="100%" height="100%">
             <BarChart data={speedData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
               <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} dy={10} />
               <YAxis hide />
               <Tooltip
                 cursor={{ fill: 'transparent' }}
                 contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
               />
               <Bar dataKey="wpm" radius={[8, 8, 0, 0]} barSize={60}>
                 {speedData.map((_, i) => (
                   <Cell key={i} fill={i === 0 ? '#cbd5e1' : i === 1 ? '#93c5fd' : '#3b82f6'} />
                 ))}
               </Bar>
             </BarChart>
           </ResponsiveContainer>
         </div>
         <div className="flex justify-around mt-4 text-center">
           <div>
             <div className="text-2xl font-bold text-slate-400">200</div>
             <div className="text-xs text-slate-400 uppercase">Baseline WPM</div>
           </div>
           <div>
             <div className="text-2xl font-bold text-primary">{displayAvgWpm || '~500'}</div>
             <div className="text-xs text-primary uppercase">New Avg WPM</div>
           </div>
           <div>
             <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{displayMaxWpm}</div>
             <div className="text-xs text-blue-600 dark:text-blue-400 uppercase">Peak WPM achieved</div>
           </div>
         </div>
         {/* Data explanation text */}
         <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-4 italic">
           {wordsRead > 0
             ? 'This is your actual reading data from your sessions.'
             : 'Default values shown — complete a story to see your real stats here.'}
         </p>
       </div>

      {/* Primary Row - More Accented */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border-2 border-blue-200 dark:border-blue-800 shadow-md flex flex-col justify-center">
          <BookOpen className="w-8 h-8 text-blue-500 mb-3" />
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{wordsRead.toLocaleString()}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Words read</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border-2 border-amber-200 dark:border-amber-800 shadow-md flex flex-col justify-center">
          <Zap className="w-8 h-8 text-amber-500 mb-3" />
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{displayMaxWpm}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Peak WPM</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border-2 border-emerald-200 dark:border-emerald-800 shadow-md flex flex-col justify-center">
          <Layers className="w-8 h-8 text-emerald-500 mb-3" />
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{sessions}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Sessions</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border-2 border-purple-200 dark:border-purple-800 shadow-md flex flex-col justify-center">
          <BookCheck className="w-8 h-8 text-purple-500 mb-3" />
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">{booksCompleted}</div>
          <div className="text-sm text-slate-600 dark:text-slate-400">Stories completed</div>
        </div>
      </div>

      {/* Secondary Row - Less Accented */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center">
          <Clock className="w-6 h-6 text-slate-400 mb-2" />
          <div className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-1">{formatTimeSaved(timeSaved)}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Time saved</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center">
          <Timer className="w-6 h-6 text-slate-400 mb-2" />
          <div className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-1">{formatDuration(timeSpentSeconds)}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Total reading time</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center">
          <Library className="w-6 h-6 text-slate-400 mb-2" />
          <div className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-1">{genresRead.length}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Genres explored</div>
        </div>
        <div className="bg-white dark:bg-slate-900 p-5 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col justify-center">
          <Layers className="w-6 h-6 text-slate-400 mb-2" />
          <div className="text-2xl font-semibold text-slate-700 dark:text-slate-300 mb-1">{avgSessionMinutes}m</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">Avg session length</div>
        </div>
      </div>
    </div>
  );
};
